/**
 * Shared regional configuration — single source for timezones, countries,
 * states/regions and currencies used by Admin Management, Platform Admin,
 * the Org Hierarchy site form and any currency/number display.
 *
 * Tenant-level values (country / currency / currency_symbol / locale) are read
 * from localStorage['tenant'] — written at login (AuthContext) and after the
 * admin saves Company Settings — the same source dateUtils.js uses.
 */

const FALLBACK_TIMEZONES = [
  'UTC',
  'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Singapore', 'Asia/Dubai',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Jakarta', 'Asia/Bangkok', 'Asia/Riyadh',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Australia/Sydney',
];

/** Full IANA list when the runtime exposes it, otherwise a curated fallback. */
export const TIMEZONES = (() => {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const list = Intl.supportedValuesOf('timeZone');
      return list.includes('UTC') ? list : ['UTC', ...list];
    }
  } catch {
    // fall through
  }
  return FALLBACK_TIMEZONES;
})();

/** Countries with first-class regional behaviour (ISO-2 code → config). */
export const SUPPORTED_COUNTRIES = [
  { code: 'IN', name: 'India', currency: 'INR', locale: 'en-IN', timezones: ['Asia/Kolkata'] },
  { code: 'MY', name: 'Malaysia', currency: 'MYR', locale: 'en-MY', timezones: ['Asia/Kuala_Lumpur'] },
  { code: 'SG', name: 'Singapore', currency: 'SGD', locale: 'en-SG', timezones: ['Asia/Singapore'] },
  { code: 'AE', name: 'United Arab Emirates', currency: 'AED', locale: 'en-AE', timezones: ['Asia/Dubai'] },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', locale: 'en-GB', timezones: ['Europe/London'] },
  { code: 'US', name: 'United States', currency: 'USD', locale: 'en-US',
    timezones: ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu'] },
];

export const CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'INR (₹)' },
  { code: 'MYR', symbol: 'RM', label: 'MYR (RM)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'SGD', symbol: 'S$', label: 'SGD (S$)' },
  { code: 'AED', symbol: 'AED', label: 'AED' },
];

export const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'en-MY', label: 'English (Malaysia)' },
  // Also accepted by the backend locale whitelist (tenant_admin.VALID_LOCALES);
  // kept so tenants that already store one of these don't lose it on save.
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'ja-JP', label: 'Japanese (Japan)' },
  { value: 'zh-CN', label: 'Chinese (China)' },
];

/**
 * `{value,label}` timezone options for selects (SearchableSelect). A stored
 * value missing from the runtime list is kept so editing never silently
 * changes it.
 */
export function timezoneOptions(current) {
  const opts = TIMEZONES.map((tz) => ({ value: tz, label: tz }));
  if (current && !TIMEZONES.includes(current)) {
    opts.unshift({ value: current, label: current });
  }
  return opts;
}

/**
 * Fill currency / currency_symbol / locale / timezone from a selected country,
 * but only where the current value is empty (or the untouched 'UTC' /
 * 'en-US' default) — never overwrite an explicit admin choice.
 */
export function prefillFromCountry(settings, countryCode) {
  const c = findCountry(countryCode);
  if (!c) return { ...settings, country: countryCode || '' };
  const next = { ...settings, country: c.code };
  if (!next.currency) {
    next.currency = c.currency;
    if (!next.currency_symbol) {
      const cur = CURRENCIES.find((x) => x.code === c.currency);
      next.currency_symbol = cur ? cur.symbol : '';
    }
  }
  // Only locales the backend whitelist accepts (LOCALES) — e.g. en-SG is not.
  if ((!next.locale || next.locale === 'en-US') && LOCALES.some((l) => l.value === c.locale)) {
    next.locale = c.locale;
  }
  if (!next.timezone || next.timezone === 'UTC') next.timezone = c.timezones[0];
  return next;
}

/** State / province / region lists for countries where the business needs them. */
export const STATES_BY_COUNTRY = {
  MY: [
    'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang',
    'Perak', 'Perlis', 'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu',
    'W.P. Kuala Lumpur', 'W.P. Labuan', 'W.P. Putrajaya',
  ],
};

/** Label for the state field per country. */
export const STATE_LABEL_BY_COUNTRY = {
  IN: 'State / UT',
  MY: 'State / Federal Territory',
};

const readTenant = () => {
  try {
    const raw = localStorage.getItem('tenant');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/** Resolve a country name / ISO-2 / alias to a SUPPORTED_COUNTRIES entry. */
export function findCountry(value) {
  if (!value) return null;
  const v = String(value).trim().toLowerCase();
  return SUPPORTED_COUNTRIES.find(
    (c) => c.code.toLowerCase() === v || c.name.toLowerCase() === v,
  ) || null;
}

/** Tenant country ISO-2 code, or '' when not configured. */
export function getTenantCountry() {
  const t = readTenant();
  return t.country ? String(t.country).toUpperCase() : '';
}

/** Tenant currency ISO code, or '' when not configured (never assume INR). */
export function getTenantCurrency() {
  const t = readTenant();
  if (t.currency) return String(t.currency).toUpperCase();
  const c = findCountry(t.country);
  return c ? c.currency : '';
}

/** Tenant locale for number formatting; falls back to the browser default. */
export function getTenantLocale() {
  const t = readTenant();
  return t.locale || undefined;
}

/**
 * Display symbol for a currency code. Tenant-configured symbol wins for the
 * tenant's own currency; unknown codes show the ISO code itself.
 */
export function getCurrencySymbol(code) {
  if (!code) return '';
  const upper = String(code).toUpperCase();
  const t = readTenant();
  if (t.currency_symbol && t.currency && String(t.currency).toUpperCase() === upper) {
    return t.currency_symbol;
  }
  const c = CURRENCIES.find((x) => x.code === upper);
  return c ? c.symbol : upper;
}

/** Number formatted with the tenant locale. */
export function formatNumber(value, options = {}) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat(getTenantLocale(), options).format(Number(value));
}

/**
 * Currency amount, e.g. "RM 1,250.00". Uses the given code or the tenant
 * currency; with neither, returns the bare number (never assumes INR).
 */
export function formatCurrency(value, code) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  const cur = code || getTenantCurrency();
  const num = formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${getCurrencySymbol(cur)} ${num}` : num;
}
